import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, DollarSign, FileText, FolderKanban, Plus } from 'lucide-react';
import { useToast } from '../context/ToastContext';
import { useLanguage } from '../context/LanguageContext';
import { exportQuotationPdf, openQuotationPreview } from '../utils/quotationTemplate';
import { useSettings } from '../context/SettingsContext';

const CURRENCIES = { USD: '$', EGP: 'EGP ', EUR: 'EUR ', SAR: 'SAR ', AED: 'AED ' };
const fmtMoney = (amount, currency = 'USD') => `${CURRENCIES[currency] || '$'}${Number(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const paidAmount = (invoice) => (invoice.installments || []).length
  ? invoice.installments.filter(i => i.status === 'paid').reduce((s, i) => s + Number(i.amount || 0), 0)
  : invoice.status === 'paid' ? Number(invoice.total || 0) : 0;
const addMoney = (obj, currency, amount) => { obj[currency || 'USD'] = (obj[currency || 'USD'] || 0) + Number(amount || 0); };
const moneyText = (obj) => Object.entries(obj).filter(([, v]) => v).map(([c, v]) => fmtMoney(v, c)).join(' / ') || fmtMoney(0);

const ClientProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { t } = useLanguage();
  const { settings } = useSettings();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');
  const [activity, setActivity] = useState({ type: 'note', title: '', notes: '', followUpAt: '' });
  const [timeline, setTimeline] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [serviceForm, setServiceForm] = useState({ serviceId: '', name: '', description: '', quantity: 1, unitPrice: '', currency: 'USD' });

  const load = () => axios.get(`/api/clients/${id}`).then(r => setClient(r.data.data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, [id]);
  useEffect(() => { axios.get(`/api/clients/${id}/timeline`).then(r => setTimeline(r.data.data)).catch(() => {}); }, [id]);
  useEffect(() => { axios.get('/api/services').then(r => setCatalog(r.data.data.filter(s => s.isActive !== false))).catch(() => {}); }, []);

  const projects = client?.projects || [];
  const clientServices = client?.clientServices || [];
  const invoices = client?.invoices || [];
  const quotations = client?.quotations || [];
  const tasks = projects.flatMap(project => (project.tasks || []).map(task => ({ ...task, project })));
  const files = (client?.folders || []).flatMap(folder => [
    ...(folder.files || []).map(file => ({ ...file, folder: folder.name })),
    ...(folder.subfolders || []).flatMap(sub => (sub.files || []).map(file => ({ ...file, folder: `${folder.name} / ${sub.name}` }))),
  ]);
  const totals = useMemo(() => invoices.reduce((acc, inv) => {
    addMoney(acc.contracts, inv.currency, inv.total);
    addMoney(acc.paid, inv.currency, paidAmount(inv));
    addMoney(acc.remaining, inv.currency, Math.max(0, Number(inv.total || 0) - paidAmount(inv)));
    return acc;
  }, { contracts: {}, paid: {}, remaining: {} }), [invoices]);

  const servicesByCurrency = useMemo(() => clientServices.reduce((acc, svc) => {
    addMoney(acc, svc.currency, Number(svc.quantity || 1) * Number(svc.unitPrice || 0));
    return acc;
  }, {}), [clientServices]);

  const addActivity = async (e) => {
    e.preventDefault();
    if (!activity.title.trim()) return;
    try {
      await axios.post(`/api/clients/${id}/activities`, { ...activity, followUpAt: activity.followUpAt || null });
      showToast('Activity added', 'success');
      setActivity({ type: 'note', title: '', notes: '', followUpAt: '' });
      load();
    } catch (err) { showToast(err.response?.data?.message || 'Failed to add activity', 'error'); }
  };

  const setSvc = (key, value) => {
    if (key === 'serviceId') {
      const svc = catalog.find(s => s.id === value);
      setServiceForm(f => ({ ...f, serviceId: value, name: svc?.name || '', description: svc?.description || '', unitPrice: svc?.defaultPrice || '', currency: svc?.currency || f.currency }));
      return;
    }
    setServiceForm(f => ({ ...f, [key]: value }));
  };

  const addClientService = async (e) => {
    e.preventDefault();
    if (!serviceForm.name.trim()) return;
    try {
      await axios.post(`/api/services/clients/${id}`, serviceForm);
      setServiceForm({ serviceId: '', name: '', description: '', quantity: 1, unitPrice: '', currency: 'USD' });
      load();
    } catch (err) { showToast(err.response?.data?.message || 'Failed to add service', 'error'); }
  };

  const deleteClientService = async (service) => {
    if (!window.confirm(`Remove "${service.name}" from this client?`)) return;
    try {
      await axios.delete(`/api/services/clients/${id}/${service.id}`);
      load();
    } catch (err) { showToast(err.response?.data?.message || 'Failed to remove service', 'error'); }
  };

  const exportCsv = () => {
    const rows = [
      ['Type', 'Name', 'Status', 'Total', 'Paid', 'Remaining'],
      ...invoices.map(inv => ['Invoice', inv.invoiceNumber, inv.status, fmtMoney(inv.total, inv.currency), fmtMoney(paidAmount(inv), inv.currency), fmtMoney(Math.max(0, Number(inv.total || 0) - paidAmount(inv)), inv.currency)]),
      ...tasks.map(task => ['Task', task.title, task.status, task.project?.name || '', task.assignee?.name || '', task.dueDate || '']),
    ];
    const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url; a.download = `${client.companyName}-summary.csv`; a.click(); URL.revokeObjectURL(url);
  };

  const quotationPayload = () => {
    const firstCurrency = clientServices[0]?.currency || 'USD';
    const items = clientServices.map(svc => ({
      description: svc.name,
      details: svc.description || '',
      qty: Number(svc.quantity || 1),
      unitPrice: Number(svc.unitPrice || 0),
    }));
    const subtotal = clientServices.reduce((sum, svc) => sum + Number(svc.quantity || 1) * Number(svc.unitPrice || 0), 0);
    return {
      quotation: { quotationNumber: 'Preview', currency: firstCurrency, status: 'draft', issueDate: new Date().toLocaleDateString() },
      client,
      items,
      totals: { subtotal, total: subtotal, taxRate: 0, currency: firstCurrency },
      settings,
    };
  };

  const createQuotation = async () => {
    try {
      await axios.post('/api/quotations', { clientId: id, status: 'draft' });
      showToast('Quotation created', 'success');
      load();
    } catch (err) { showToast(err.response?.data?.message || 'Failed to create quotation', 'error'); }
  };

  const downloadQuotationPdf = async () => {
    try {
      await exportQuotationPdf(quotationPayload());
    } catch (err) {
      showToast('Failed to export PDF', 'error');
    }
  };

  if (loading) return <div style={{ padding: 32, color: 'var(--text-muted)' }}>Loading...</div>;
  if (!client) return <div style={{ padding: 32 }}>Client not found</div>;

  return (
    <div className="page-content">
      <button className="btn btn-secondary btn-sm mb-16" onClick={() => navigate('/clients')}><ArrowLeft size={14} /> Back</button>
      <div className="flex-between mb-24">
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700 }}>{client.companyName}</h1>
          <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>{client.contactPerson} · {client.email}</p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn btn-secondary" onClick={() => openQuotationPreview(quotationPayload())}><FileText size={15} /> Preview</button>
          <button className="btn btn-secondary" onClick={downloadQuotationPdf}><FileText size={15} /> Export PDF</button>
          <button className="btn btn-primary" onClick={createQuotation}><Plus size={15} /> Save Quotation</button>
          <button className="btn btn-secondary" onClick={exportCsv}><FileText size={15} /> Export CSV</button>
        </div>
      </div>

      <div className="stats-grid">
        {[{ label: t('services'), value: clientServices.length, icon: <FolderKanban /> }, { label: t('contractValue'), value: moneyText(servicesByCurrency), icon: <DollarSign /> }, { label: t('paid'), value: moneyText(totals.paid), icon: <FileText /> }, { label: t('remaining'), value: moneyText(totals.remaining), icon: <DollarSign /> }].map(s => (
          <div className="stat-card" key={s.label}><div className="stat-icon">{s.icon}</div><div className="stat-value" style={{ fontSize: 18 }}>{s.value}</div><div className="stat-label">{s.label}</div></div>
        ))}
      </div>

      <div className="search-bar">
        {['overview', 'services', 'quotations', 'tasks', 'finance', 'files', 'activity', 'timeline'].map(k => (
          <button key={k} className={`btn btn-sm ${tab === k ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setTab(k)}>{t(k) || k}</button>
        ))}
      </div>

      {tab === 'overview' && <div className="grid-2"><ListCard title="Recent Tasks" items={tasks.slice(0, 5).map(t => `${t.title} · ${t.status}`)} /><ListCard title="Recent Invoices" items={invoices.slice(0, 5).map(i => `${i.invoiceNumber} · ${fmtMoney(i.total, i.currency)} · ${i.status}`)} /></div>}
      {tab === 'services' && (
        <div className="grid-2">
          <div className="card">
            <div className="card-title mb-16">Add Service</div>
            <form onSubmit={addClientService}>
              <div className="input-group"><label className="input-label">Service</label><select className="input select" value={serviceForm.serviceId} onChange={e => setSvc('serviceId', e.target.value)}><option value="">Custom service</option>{catalog.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}</select></div>
              <div className="input-group"><label className="input-label">Name</label><input className="input" value={serviceForm.name} onChange={e => setSvc('name', e.target.value)} /></div>
              <div className="input-group"><label className="input-label">Details</label><textarea className="input" rows={3} value={serviceForm.description || ''} onChange={e => setSvc('description', e.target.value)} /></div>
              <div className="grid-2">
                <div className="input-group"><label className="input-label">Qty</label><input className="input" type="number" min="1" value={serviceForm.quantity} onChange={e => setSvc('quantity', e.target.value)} /></div>
                <div className="input-group"><label className="input-label">Price</label><input className="input" type="number" min="0" step="0.01" value={serviceForm.unitPrice} onChange={e => setSvc('unitPrice', e.target.value)} /></div>
              </div>
              <button className="btn btn-primary"><Plus size={14} /> Add</button>
            </form>
          </div>
          <Table rows={clientServices.map(s => [s.name, s.description || '-', s.status, Number(s.quantity || 1), fmtMoney(s.unitPrice, s.currency), fmtMoney(Number(s.quantity || 1) * Number(s.unitPrice || 0), s.currency), <button className="btn btn-secondary btn-sm" onClick={() => deleteClientService(s)}>Remove</button>])} headers={['Service', 'Details', 'Status', 'Qty', 'Price', 'Total', 'Actions']} />
        </div>
      )}
      {tab === 'quotations' && <Table rows={quotations.map(q => [q.quotationNumber, q.status, q.issueDate || '-', q.validUntil || '-', fmtMoney(q.total, q.currency)])} headers={['Quotation', 'Status', 'Issue Date', 'Valid Until', 'Total']} />}
      {tab === 'tasks' && <Table rows={tasks.map(t => [t.title, t.project?.name, t.status, t.priority, t.assignee?.name || 'Unassigned', t.dueDate || '-'])} headers={['Task', 'Project', 'Status', 'Priority', 'Assignee', 'Due']} onRow={(idx) => navigate(`/projects/${tasks[idx].projectId}`)} />}
      {tab === 'finance' && <FinanceTimeline invoices={invoices} />}
      {tab === 'files' && <Table rows={files.map(f => [f.name, f.folder, f.project?.name || '-', f.invoice?.invoiceNumber || '-', new Date(f.createdAt).toLocaleDateString()])} headers={['File', 'Folder', 'Project', 'Invoice', 'Uploaded']} />}
      {tab === 'activity' && (
        <div className="grid-2">
          <div className="card">
            <div className="card-title mb-16">Add Activity</div>
            <form onSubmit={addActivity}>
              <div className="input-group"><label className="input-label">Type</label><select className="input select" value={activity.type} onChange={e => setActivity(a => ({ ...a, type: e.target.value }))}>{['note','call','meeting','follow_up'].map(v => <option key={v} value={v}>{v.replace('_',' ')}</option>)}</select></div>
              <div className="input-group"><label className="input-label">Title</label><input className="input" value={activity.title} onChange={e => setActivity(a => ({ ...a, title: e.target.value }))} /></div>
              <div className="input-group"><label className="input-label">Notes</label><textarea className="input" rows={3} value={activity.notes} onChange={e => setActivity(a => ({ ...a, notes: e.target.value }))} /></div>
              <div className="input-group"><label className="input-label">Follow-up</label><input type="datetime-local" className="input" value={activity.followUpAt} onChange={e => setActivity(a => ({ ...a, followUpAt: e.target.value }))} /></div>
              <button className="btn btn-primary"><Plus size={14} /> Add</button>
            </form>
          </div>
          <ListCard title="Timeline" items={(client.activities || []).map(a => `${new Date(a.createdAt).toLocaleString()} · ${a.type} · ${a.title} · ${a.user?.name || ''}`)} />
        </div>
      )}
      {tab === 'timeline' && <ListCard title="Unified Timeline" items={timeline.map(item => `${new Date(item.date).toLocaleString()} · ${item.type} · ${item.title}${item.description ? ` · ${item.description}` : ''}`)} />}
    </div>
  );
};

const ListCard = ({ title, items }) => <div className="card"><div className="card-title mb-16">{title}</div>{items.length ? items.map((i, idx) => <div key={idx} style={{ padding: '10px 0', borderBottom: '1px solid var(--border)', fontSize: 13 }}>{i}</div>) : <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>No data yet</div>}</div>;

const Table = ({ headers, rows, onRow }) => <div className="card" style={{ padding: 0 }}><div className="table-wrap"><table><thead><tr>{headers.map(h => <th key={h}>{h}</th>)}</tr></thead><tbody>{rows.map((r, i) => <tr key={i} onClick={() => onRow?.(i)} style={{ cursor: onRow ? 'pointer' : 'default' }}>{r.map((c, j) => <td key={j}>{c}</td>)}</tr>)}</tbody></table></div></div>;

const FinanceTimeline = ({ invoices }) => <div className="grid-2"><Table headers={['Invoice', 'Status', 'Total', 'Paid', 'Remaining']} rows={invoices.map(i => [i.invoiceNumber, i.status, fmtMoney(i.total, i.currency), fmtMoney(paidAmount(i), i.currency), fmtMoney(Math.max(0, Number(i.total || 0) - paidAmount(i)), i.currency)])} /><ListCard title="Payment History" items={invoices.flatMap(i => (i.paymentHistory || []).map(h => `${i.invoiceNumber} · ${h.description} · ${fmtMoney(h.amount, i.currency)} · ${new Date(h.paidAt).toLocaleString()} · ${h.markedBy?.name || ''}`))} /></div>;

export default ClientProfile;
